import { useMemo, useState, useCallback } from 'react';
import { View, TouchableOpacity, ScrollView, ActivityIndicator, TextInput, Alert } from 'react-native';
import { Stack, useLocalSearchParams, useRouter } from 'expo-router';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { ArrowLeft, MessageSquareQuote, Plus, Star, X } from 'lucide-react-native';
import { Text } from '@/components';
import { useAuth } from '@/context/AuthContext';
import { useTrailReviews, useSubmitReview } from '@/lib/queries/reviews';
import { useTranslation } from 'react-i18next';
import LottieLoader from '@/components/LottieLoader';
import colors from '@/constants/colors';
import styles from './reviews.styles';
import * as Haptics from 'expo-haptics';

type TrailReview = {
  id: string;
  rating: number;
  content?: string;
  createdAt?: string;
  updatedAt?: string;
};

function formatReviewDate(value?: string, t?: (key: string) => string) {
  if (!value) return t?.('trailReviews.unknownDate') ?? 'Unknown date';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return t?.('trailReviews.unknownDate') ?? 'Unknown date';
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

export default function TrailReviewsScreen() {
  const { id } = useLocalSearchParams<{ id: string }>();
  const { session } = useAuth();
  const router = useRouter();
  const insets = useSafeAreaInsets();
  const { t } = useTranslation();
  const token = session?.accessToken;
  const trailId = typeof id === 'string' ? id : undefined;

  // React Query–backed reviews data
  const reviewsQuery = useTrailReviews(trailId, token);
  const submitReviewMutation = useSubmitReview(trailId, token);

  const reviews: TrailReview[] = reviewsQuery.data ?? [];
  const isLoading = reviewsQuery.isLoading;
  const errorMessage = reviewsQuery.error ? t('trailReviews.unableToLoad') : null;

  const [showCompose, setShowCompose] = useState(false);
  const [draftRating, setDraftRating] = useState(0);
  const [draftContent, setDraftContent] = useState('');

  const averageRating = useMemo(() => {
    if (reviews.length === 0) return null;
    const total = reviews.reduce((sum, review) => sum + (Number.isFinite(review.rating) ? review.rating : 0), 0);
    return total / reviews.length;
  }, [reviews]);

  const handleSubmitReview = useCallback(async () => {
    if (!trailId) {
      Alert.alert(t('common.error'), t('trailReviews.trailIdMissing'));
      return;
    }
    if (!token) {
      Alert.alert(t('trailReviews.signInRequired'), t('trailReviews.signInRequiredMessage'));
      return;
    }
    if (!draftRating) {
      Alert.alert(t('trailReviews.ratingRequired'), t('trailReviews.ratingRequiredMessage'));
      return;
    }

    try {
      await submitReviewMutation.mutateAsync({
        rating: draftRating,
        ...(draftContent.trim() ? { content: draftContent.trim() } : {}),
      });

      Haptics.notificationAsync(Haptics.NotificationFeedbackType.Success);
      setDraftRating(0);
      setDraftContent('');
      setShowCompose(false);
    } catch (error) {
      console.error('Trail review submit error:', error);
      Haptics.notificationAsync(Haptics.NotificationFeedbackType.Error);
      Alert.alert(t('common.error'), t('trailReviews.unableToSubmit'));
    }
  }, [draftContent, draftRating, trailId, token, submitReviewMutation, t]);

  const isSubmitting = submitReviewMutation.isPending;

  return (
    <View style={styles.container}>
      <Stack.Screen options={{ headerShown: false }} />

      {isLoading ? (
        <View style={styles.loadingContainer}>
          <LottieLoader size={120} />
        </View>
      ) : errorMessage ? (
        <ScrollView contentContainerStyle={[styles.content, { paddingTop: insets.top + 12 }]}>
          <View style={styles.errorCard}>
            <Text style={styles.errorText}>{errorMessage}</Text>
            <TouchableOpacity style={styles.retryButton} onPress={() => reviewsQuery.refetch()}>
              <Text style={styles.retryText}>{t('trailReviews.tryAgain')}</Text>
            </TouchableOpacity>
          </View>
        </ScrollView>
      ) : (
        <ScrollView contentContainerStyle={[styles.content, { paddingTop: insets.top + 12 }]}>
          <View style={styles.header}>
            <TouchableOpacity style={styles.backButton} onPress={() => router.back()}>
              <ArrowLeft size={22} color={colors.accentPrimary} />
            </TouchableOpacity>
            <View style={styles.headerTextWrap}>
              <Text style={styles.title}>{t('trailReviews.title')}</Text>
              <Text style={styles.subtitle}>{t('trailReviews.reviewCount', { count: reviews.length })}</Text>
            </View>
            <TouchableOpacity
              style={styles.addReviewButton}
              onPress={() => {
                Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Light);
                setShowCompose((prev) => !prev);
              }}
            >
              {showCompose ? (
                <X size={20} color={colors.backgroundPrimary} />
              ) : (
                <Plus size={20} color={colors.backgroundPrimary} />
              )}
            </TouchableOpacity>
          </View>

          {showCompose && <View style={styles.composeCard}>
            <Text style={styles.composeTitle}>{t('trailReviews.leaveAReview')}</Text>
            <Text style={styles.composeSubtitle}>{t('trailReviews.tellOtherWalkers')}</Text>

            <View style={styles.composeStarsRow}>
              {[1, 2, 3, 4, 5].map((star) => {
                const active = star <= draftRating;
                return (
                  <TouchableOpacity
                    key={`draft-star-${star}`}
                    style={styles.composeStarButton}
                    onPress={() => {
                      Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Light);
                      setDraftRating(star);
                    }}
                    disabled={isSubmitting}
                  >
                    <Star
                      size={28}
                      color={active ? colors.accentPrimary : colors.backgroundSecondaryVarient}
                      fill={active ? colors.accentPrimary : 'none'}
                      strokeWidth={2}
                    />
                  </TouchableOpacity>
                );
              })}
            </View>

            <TextInput
              style={styles.composeInput}
              placeholder={t('trailReviews.reviewPlaceholder')}
              placeholderTextColor={colors.textMuted}
              value={draftContent}
              onChangeText={setDraftContent}
              editable={!isSubmitting}
              multiline
              numberOfLines={5}
              textAlignVertical="top"
              maxLength={500}
            />

            <View style={styles.composeFooter}>
              <Text style={styles.composeHint}>{t('trailReviews.characterCount', { count: draftContent.trim().length })}</Text>
              <TouchableOpacity
                style={[
                  styles.submitButton,
                  (!draftRating || isSubmitting) && styles.submitButtonDisabled,
                ]}
                onPress={handleSubmitReview}
                disabled={!draftRating || isSubmitting}
              >
                {isSubmitting ? (
                  <ActivityIndicator size="small" color={colors.backgroundPrimary} />
                ) : (
                  <Text style={styles.submitButtonText}>{t('trailReviews.postReview')}</Text>
                )}
              </TouchableOpacity>
            </View>
          </View>}

          {reviews.length === 0 ? (
            <View style={styles.emptyCard}>
              <MessageSquareQuote size={28} color={colors.textMuted} />
              <Text style={styles.emptyTitle}>{t('trailReviews.noReviewsYet')}</Text>
              <Text style={styles.emptyText}>{t('trailReviews.beTheFirst')}</Text>
            </View>
          ) : (
            <>
              <View style={styles.summaryCard}>
                <Text style={styles.summaryLabel}>{t('trailReviews.averageRating')}</Text>
                <Text style={styles.summaryValue}>{averageRating ? averageRating.toFixed(1) : 'N/A'}</Text>
                <Text style={styles.summaryMeta}>{t('trailReviews.reviewCount', { count: reviews.length })}</Text>
              </View>

              {reviews.map((review, index) => (
                <View key={`${review.id}-${index}`} style={styles.reviewCard}>
                  <View style={styles.reviewHeader}>
                    <View style={styles.starsRow}>
                      {[1, 2, 3, 4, 5].map((star) => (
                        <Star
                          key={`${review.id}-${index}-${star}`}
                          size={18}
                          color={star <= review.rating ? colors.accentPrimary : colors.backgroundSecondaryVarient}
                          fill={star <= review.rating ? colors.accentPrimary : 'none'}
                          strokeWidth={2}
                        />
                      ))}
                      <Text style={styles.ratingText}>{`${review.rating}/5`}</Text>
                    </View>
                    <Text style={styles.dateText}>{formatReviewDate(review.createdAt, t)}</Text>
                  </View>

                  <Text style={styles.reviewContent}>{review.content?.trim() || t('trailReviews.noWrittenReview')}</Text>
                </View>
              ))}
            </>
          )}
        </ScrollView>
      )}
    </View>
  );
}